import React, { useRef, useEffect } from 'react';

const NUM_RAYS = 32;
const RAY_WIDTH = 8;
const COLORS = [
    ['#3F013D', '#ae4094'], // Deep purple to accent purple (your brand colors)
    ['#1a0033', '#6b0f5e'], // Dark purple to deep magenta
    ['#2d1b69', '#8b5cf6'], // Deep blue-purple to violet
    ['#0f172a', '#475569'], // Dark slate to medium slate
    ['#581c87', '#c084fc'], // Deep purple to light purple
    ['#1e1b4b', '#6366f1'], // Dark indigo to indigo
    ['#374151', '#9ca3af'], // Dark gray to light gray
    ['#4c1d95', '#a855f7'], // Deep violet to purple
    ['#5b1065', '#d946ef'], // Rich purple to bright magenta
    ['#701a75', '#f472b6'], // Deep magenta to pink-magenta
    ['#1e3a8a', '#3b82f6'], // Deep blue to bright blue
    ['#0c4a6e', '#0ea5e9'], // Dark blue to sky blue
    ['#164e63', '#06b6d4'], // Dark cyan to cyan
    ['#0f172a', '#1e40af'], // Very dark blue to blue
    ['#312e81', '#6366f1'], // Dark indigo to indigo
];

function lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
}

// Geometric pattern functions
const PATTERNS = {
    sine: (i: number, time: number, totalRays: number) => {
        return Math.sin(time / 4000 + i * 0.3) * 15;
    },

    triangle: (i: number, time: number, totalRays: number) => {
        const phase = (time / 6000 + i / totalRays) % 1;
        return (phase < 0.5 ? phase * 2 : 2 - phase * 2) * 20 - 10;
    },

    circle: (i: number, time: number, totalRays: number) => {
        const angle = (i / totalRays) * Math.PI * 2 + time / 5000;
        return Math.cos(angle) * 25;
    },

    diamond: (i: number, time: number, totalRays: number) => {
        const progress = (i / totalRays + time / 8000) % 1;
        if (progress < 0.25) return progress * 4 * 20;
        else if (progress < 0.5) return (0.5 - progress) * 4 * 20;
        else if (progress < 0.75) return (progress - 0.5) * 4 * -20;
        else return (1 - progress) * 4 * -20;
    },

    wave: (i: number, time: number, totalRays: number) => {
        return Math.sin(time / 3000 + i * 0.4) * Math.cos(time / 6000) * 30;
    },

    spiral: (i: number, time: number, totalRays: number) => {
        const spiralTime = time / 4000;
        const angle = (i / totalRays) * Math.PI * 4 + spiralTime;
        const radius = Math.sin(spiralTime) * 20;
        return Math.sin(angle) * radius;
    },

    pulse: (i: number, time: number, totalRays: number) => {
        const pulseStrength = Math.abs(Math.sin(time / 4000));
        const centerDistance = Math.abs(i - totalRays / 2) / (totalRays / 2);
        return Math.sin(time / 2000 + centerDistance * Math.PI * 2) * pulseStrength * 25;
    },

    zigzag: (i: number, time: number, totalRays: number) => {
        const progress = (time / 6000 + i / totalRays * 2) % 2;
        return progress < 1 ? progress * 20 - 10 : (2 - progress) * 20 - 10;
    }
};

const PATTERN_NAMES = Object.keys(PATTERNS) as Array<keyof typeof PATTERNS>;
const PATTERN_DURATION = 8000; // 8 seconds per pattern

const AnimatedBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mouse = useRef({ x: 0.5, y: 0.5 });
    const hoverInfo = useRef<{ rayIndex: number; localY: number } | null>(null);
    const glowStates = useRef<Array<{ intensity: number; lastActiveTime: number }>>([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationId: number;
        let width = window.innerWidth;
        let height = window.innerHeight;
        let rayPositions: Array<{ x: number; rayHeight: number }> = [];

        // Initialize glow states
        if (glowStates.current.length === 0) {
            for (let i = 0; i < NUM_RAYS; i++) {
                glowStates.current.push({ intensity: 0, lastActiveTime: 0 });
            }
        }

        function resize() {
            const canvas = canvasRef.current;
            if (!canvas) return;
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
            ctx = canvas.getContext('2d');
        }
        resize();
        window.addEventListener('resize', resize);

        // Use global mouse events to avoid interference from blur layer
        function onMouseMove(e: MouseEvent) {
            const mouseX = e.clientX;
            const mouseY = e.clientY;

            mouse.current.x = mouseX / width;
            mouse.current.y = mouseY / height;

            // Check if mouse is over any ray
            hoverInfo.current = null;

            for (let i = 0; i < rayPositions.length; i++) {
                const ray = rayPositions[i];
                const rayTop = height - ray.rayHeight;
                const rayBottom = height;
                const rayLeft = ray.x - RAY_WIDTH * 2; // Expand hit area
                const rayRight = ray.x + RAY_WIDTH * 2; // Expand hit area

                // Check if mouse is within ray bounds
                if (mouseX >= rayLeft && mouseX <= rayRight &&
                    mouseY >= rayTop && mouseY <= rayBottom) {
                    // Calculate local position within the ray (0 = top, 1 = bottom)
                    const localY = (mouseY - rayTop) / ray.rayHeight;
                    hoverInfo.current = { rayIndex: i, localY };
                    break;
                }
            }
        }

        function onMouseLeave() {
            hoverInfo.current = null;
        }

        // Use window events instead of canvas events
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseleave', onMouseLeave);

        function getPatternMovement(i: number, time: number) {
            // Determine current and next pattern
            const totalCycleTime = PATTERN_DURATION * PATTERN_NAMES.length;
            const cycleProgress = (time % totalCycleTime) / totalCycleTime;
            const currentPatternIndex = Math.floor(cycleProgress * PATTERN_NAMES.length);
            const nextPatternIndex = (currentPatternIndex + 1) % PATTERN_NAMES.length;

            // Calculate transition progress within current pattern
            const patternProgress = (cycleProgress * PATTERN_NAMES.length) % 1;
            const transitionStart = 0.7; // Start transitioning at 70% through pattern
            const transitionProgress = patternProgress > transitionStart
                ? (patternProgress - transitionStart) / (1 - transitionStart)
                : 0;

            const currentPattern = PATTERN_NAMES[currentPatternIndex];
            const nextPattern = PATTERN_NAMES[nextPatternIndex];

            const currentMovement = PATTERNS[currentPattern](i, time, NUM_RAYS);
            const nextMovement = PATTERNS[nextPattern](i, time, NUM_RAYS);

            // Smooth transition between patterns
            return lerp(currentMovement, nextMovement, transitionProgress);
        }

        function drawRays(time: number) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = '#111';
            ctx.fillRect(0, 0, width, height);

            // Clear ray positions for hover detection
            rayPositions = [];

            // Calculate all ray positions first for proximity detection
            const rayData: Array<{
                x: number;
                rayHeight: number;
                tipY: number;
                colorIdx: number;
                alpha: number;
                rayWidth: number;
                shouldGlow: boolean;
                glowIntensity: number;
            }> = [];

            for (let i = 0; i < NUM_RAYS; i++) {
                const baseX = lerp(0, width, i / NUM_RAYS);

                // Apply geometric pattern movement
                const patternMovement = getPatternMovement(i, time);
                const mouseInfluence = (mouse.current.x - 0.5) * 40;
                const x = baseX + patternMovement + mouseInfluence;

                const rayHeight = lerp(height * 0.5, height, Math.abs(Math.sin(time / 3000 + i)));
                const colorIdx = i % COLORS.length;
                const tipY = height - rayHeight;
                const alpha = 0.3 + 0.2 * Math.sin(time / 2000 + i);
                const rayWidth = RAY_WIDTH;

                rayData.push({
                    x,
                    rayHeight,
                    tipY,
                    colorIdx,
                    alpha,
                    rayWidth,
                    shouldGlow: false,
                    glowIntensity: 0
                });

                // Store ray position for hover detection
                rayPositions.push({ x, rayHeight });
            }

            // Check for tip proximity and calculate glow intensity
            const proximityThreshold = 65; // pixels - increased from 30 for earlier glow activation
            const fadeoutDuration = 800; // milliseconds

            for (let i = 0; i < rayData.length; i++) {
                let totalProximity = 0;
                let isCurrentlyInProximity = false;

                for (let j = 0; j < rayData.length; j++) {
                    if (i === j) continue;

                    const heightDiff = Math.abs(rayData[i].tipY - rayData[j].tipY);
                    if (heightDiff <= proximityThreshold) {
                        const proximityFactor = 1 - (heightDiff / proximityThreshold);
                        totalProximity += proximityFactor;
                        isCurrentlyInProximity = true;
                    }
                }

                // Update glow state with smooth transitions
                if (isCurrentlyInProximity) {
                    // Ray is currently in proximity - update active intensity
                    const targetIntensity = Math.min(totalProximity / 3, 1);
                    glowStates.current[i].intensity = targetIntensity;
                    glowStates.current[i].lastActiveTime = time;
                    rayData[i].shouldGlow = true;
                    rayData[i].glowIntensity = targetIntensity;
                } else {
                    // Ray is not in proximity - check if we should fade out
                    const timeSinceActive = time - glowStates.current[i].lastActiveTime;

                    if (timeSinceActive < fadeoutDuration && glowStates.current[i].intensity > 0) {
                        // Still in fadeout period - gradually reduce intensity
                        const fadeProgress = timeSinceActive / fadeoutDuration;
                        const currentIntensity = glowStates.current[i].intensity * (1 - fadeProgress);

                        if (currentIntensity > 0.05) { // Only glow if intensity is meaningful
                            rayData[i].shouldGlow = true;
                            rayData[i].glowIntensity = currentIntensity;
                        }
                    } else {
                        // Fadeout complete
                        glowStates.current[i].intensity = 0;
                        rayData[i].shouldGlow = false;
                        rayData[i].glowIntensity = 0;
                    }
                }
            }

            // Now draw all rays with their calculated glow states
            for (let i = 0; i < rayData.length; i++) {
                const ray = rayData[i];

                const grad = ctx.createLinearGradient(ray.x, height - ray.rayHeight, ray.x, height);
                grad.addColorStop(0, COLORS[ray.colorIdx][0]);
                grad.addColorStop(1, COLORS[ray.colorIdx][1]);

                ctx.save();

                // Check if this ray is being hovered
                const isHovered = hoverInfo.current && hoverInfo.current.rayIndex === i;

                if (isHovered) {
                    const localY = hoverInfo.current!.localY;
                    const glowCenter = height - ray.rayHeight + (localY * ray.rayHeight);

                    // MUCH MORE INTENSE GLOW EFFECTS

                    // Outer glow - largest radius
                    const outerGlow = ctx.createRadialGradient(
                        ray.x, glowCenter, 0,
                        ray.x, glowCenter, 120
                    );
                    outerGlow.addColorStop(0, COLORS[ray.colorIdx][0] + 'FF'); // Full opacity
                    outerGlow.addColorStop(0.3, COLORS[ray.colorIdx][1] + 'AA'); // 66% opacity
                    outerGlow.addColorStop(1, 'transparent');

                    ctx.globalAlpha = 1.0;
                    ctx.fillStyle = outerGlow;
                    ctx.beginPath();
                    ctx.arc(ray.x, glowCenter, 120, 0, Math.PI * 2);
                    ctx.fill();

                    // Middle glow - more intense
                    const middleGlow = ctx.createRadialGradient(
                        ray.x, glowCenter, 0,
                        ray.x, glowCenter, 60
                    );
                    middleGlow.addColorStop(0, COLORS[ray.colorIdx][1] + 'FF'); // Full opacity
                    middleGlow.addColorStop(0.5, COLORS[ray.colorIdx][0] + 'CC'); // 80% opacity
                    middleGlow.addColorStop(1, 'transparent');

                    ctx.globalAlpha = 1.0;
                    ctx.fillStyle = middleGlow;
                    ctx.beginPath();
                    ctx.arc(ray.x, glowCenter, 60, 0, Math.PI * 2);
                    ctx.fill();

                    // Inner core - extremely bright
                    const innerGlow = ctx.createRadialGradient(
                        ray.x, glowCenter, 0,
                        ray.x, glowCenter, 30
                    );
                    innerGlow.addColorStop(0, '#FFFFFF'); // Pure white center
                    innerGlow.addColorStop(0.3, COLORS[ray.colorIdx][1] + 'FF');
                    innerGlow.addColorStop(1, COLORS[ray.colorIdx][0] + 'BB');

                    ctx.globalAlpha = 1.0;
                    ctx.fillStyle = innerGlow;
                    ctx.beginPath();
                    ctx.arc(ray.x, glowCenter, 30, 0, Math.PI * 2);
                    ctx.fill();

                    // Enhance the ray brightness in the glow area (but keep same width)
                    const glowStart = Math.max(0, localY - 0.4);
                    const glowEnd = Math.min(1, localY + 0.4);
                    const enhancedGrad = ctx.createLinearGradient(
                        ray.x, height - ray.rayHeight + (glowStart * ray.rayHeight),
                        ray.x, height - ray.rayHeight + (glowEnd * ray.rayHeight)
                    );
                    enhancedGrad.addColorStop(0, COLORS[ray.colorIdx][0] + '66');
                    enhancedGrad.addColorStop(0.5, '#FFFFFF'); // White hot center
                    enhancedGrad.addColorStop(1, COLORS[ray.colorIdx][0] + '66');

                    ctx.globalAlpha = 0.8;
                    ctx.shadowColor = COLORS[ray.colorIdx][1];
                    ctx.shadowBlur = 40;
                    ctx.fillStyle = enhancedGrad;
                    ctx.beginPath();
                    ctx.rect(
                        ray.x - ray.rayWidth / 2, // Use normal ray width
                        height - ray.rayHeight + (glowStart * ray.rayHeight),
                        ray.rayWidth, // Use normal ray width
                        (glowEnd - glowStart) * ray.rayHeight
                    );
                    ctx.fill();
                }

                // Draw main ray
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.globalAlpha = ray.alpha;
                ctx.beginPath();
                ctx.rect(ray.x - ray.rayWidth / 2, height - ray.rayHeight, ray.rayWidth, ray.rayHeight);
                ctx.fillStyle = grad;
                ctx.fill();

                // Add proximity-based tip glow with smooth transitions
                if (ray.shouldGlow) {
                    const tipGlow = ctx.createRadialGradient(
                        ray.x, ray.tipY, 0,
                        ray.x, ray.tipY, 35 * ray.glowIntensity
                    );
                    const baseOpacity = Math.floor(ray.glowIntensity * 180); // Max ~70% opacity
                    const secondaryOpacity = Math.floor(ray.glowIntensity * 120); // Max ~47% opacity

                    tipGlow.addColorStop(0, COLORS[ray.colorIdx][0] + baseOpacity.toString(16).padStart(2, '0'));
                    tipGlow.addColorStop(0.6, COLORS[ray.colorIdx][1] + secondaryOpacity.toString(16).padStart(2, '0'));
                    tipGlow.addColorStop(1, 'transparent');

                    const glowAlpha = 0.6 + 0.4 * ray.glowIntensity * Math.sin(time / 800 + i * 0.3);
                    ctx.globalAlpha = glowAlpha;
                    ctx.fillStyle = tipGlow;
                    ctx.beginPath();
                    ctx.arc(ray.x, ray.tipY, 35 * ray.glowIntensity, 0, Math.PI * 2);
                    ctx.fill();
                }

                ctx.restore();
            }
        }

        function animate(time: number) {
            drawRays(time);
            animationId = requestAnimationFrame(animate);
        }
        animate(0);

        return () => {
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseleave', onMouseLeave);
            cancelAnimationFrame(animationId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 0,
                pointerEvents: 'none', // Changed to none to avoid blocking events
                background: '#111',
                cursor: 'none',
            }}
            tabIndex={-1}
            aria-hidden="true"
        />
    );
};

export default AnimatedBackground; 